"use client";

import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Shield, Smartphone, ExternalLink } from 'lucide-react';
import { showError, showSuccess } from '@/utils/toast';
import { QRCodeSVG } from 'qrcode.react';

interface MFASettingsProps {
  mfaEnabled: boolean;
  onMFAChange: (enabled: boolean) => void;
}

const MFASettings: React.FC<MFASettingsProps> = ({ mfaEnabled, onMFAChange }: MFASettingsProps) => {
  const { user } = useAuth();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [enrollData, setEnrollData] = useState<any>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otp, setOtp] = useState('');

  useEffect(() => {
    const fetchMFAData = async () => {
      if (!user) return;

      try {
        const { data, error } = await (supabase as any).auth.mfa.listFactors();
        if (error) throw error;
        // Check if MFA is already enrolled
        const isEnrolled = !!data && Array.isArray((data as any).all) && (data as any).all.length > 0;
        onMFAChange(isEnrolled);
      } catch (err: any) {
        console.error('Error listing MFA factors:', err);
        showError('Failed to check MFA status');
      }
    };

    fetchMFAData();
  }, [user, onMFAChange]);

  const enrollMFA = async () => {
    if (!user) return;

    setEnrolling(true);
    try {
      const { data, error } = await (supabase as any).auth.mfa.enroll({ factorType: "totp" });
      if (error) throw error;

      setQrCode((data as any)?.totp?.qr_code ?? "");
      setRecoveryCodes((data as any)?.recoveryCodes ?? "");
      setEnrollData(data);
      showSuccess('MFA enrollment started. Scan the QR code and save recovery codes!');
    } catch (err: any) {
      console.error('MFA enrollment error:', err);
      showError('Failed to enroll MFA');
    } finally {
      setEnrolling(false);
    }
  };

  const verifyMFA = async () => {
    if (!user || !otp) return;

    setVerifying(true);
    try {
      const { error } = await (supabase as any).auth.mfa.verify({ factorId: enrollData?.id ?? "", code: otp });
      if (error) throw error;

      showSuccess('MFA verified and enabled!');
      onMFAChange(true);
    } catch (err: any) {
      console.error('MFA verification error:', err);
      showError('Failed to verify MFA');
    } finally {
      setVerifying(false);
    }
  };

  const unenrollMFA = async () => {
    if (!user) return;

    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: user.id });
      if (error) throw error;

      showSuccess('MFA disabled!');
      onMFAChange(false);
    } catch (err: any) {
      console.error('MFA unenrollment error:', err);
      showError('Failed to disable MFA');
    }
  };

  return (
    <Card>
      <CardHeader className="flex items-center space-x-2">
        <Shield className="h-5 w-5" />
        <CardTitle>Multi-Factor Authentication</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {mfaEnabled ? (
          <>
            <Badge variant="default">Enabled</Badge>
            <p className="text-sm text-muted-foreground">
              MFA is enabled for your account. For added security, you will be prompted for a code from your authenticator app when you sign in.
            </p>
            <Button variant="destructive" onClick={unenrollMFA}>Disable MFA</Button>
          </>
        ) : (
          <>
            <Badge variant="secondary">Disabled</Badge>
            <p className="text-sm text-muted-foreground">
              Enable multi-factor authentication for enhanced security.
            </p>
            <Button onClick={enrollMFA} disabled={enrolling}>
              {enrolling ? "Enrolling..." : "Enroll MFA"}
            </Button>
            {qrCode && (
              <div className="space-y-2">
                <QRCodeSVG value={qrCode} size={256} level="H" />
                <p className="text-sm text-muted-foreground">Scan this QR code with your authenticator app (e.g., Google Authenticator, Authy).</p>
                <Input
                  type="text"
                  placeholder="Enter verification code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
                <Button onClick={verifyMFA} disabled={verifying}>
                  {verifying ? "Verifying..." : "Verify"}
                </Button>
                {recoveryCodes.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Recovery Codes:</p>
                    <ul className="list-disc pl-5 text-sm text-muted-foreground">
                      {recoveryCodes.map((code, i) => (
                        <li key={i}>{code}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-red-500">
                      Save these codes in a safe place! They can be used to access your account if you lose access to your authenticator app.
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <p className="text-xs text-muted-foreground">
          <Smartphone className="inline-block h-3 w-3 mr-1" />
          We recommend using an authenticator app like Authy or Google Authenticator.
          <a href="https://support.google.com/accounts/answer/185839" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
            Learn more <ExternalLink className="h-3 w-3 inline-block" />
          </a>
        </p>
      </CardContent>
    </Card>
  );
};

export default MFASettings;